import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button, { buttonClasses } from '@mui/material/Button';

import { CONFIG } from 'src/global-config';

import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';
import { useColorScheme } from '@mui/material';
import { useLocale } from 'src/locales';
import { useEffect, useRef, useState } from 'react';

// ----------------------------------------------------------------------

const AppStoreButton = styled((props) => (
  <Button {...props}>
    <div>
      <Box component="span" sx={{ opacity: 0.72, display: 'block', typography: 'caption' }}>
        {props.caption}
      </Box>

      <Box component="span" sx={{ mt: -0.5, typography: 'h6' }}>
        {props.title}
      </Box>
    </div>
  </Button>
))(({ theme }) => ({
  flexShrink: 0,
  padding: '5px 12px',
  color: theme.vars.palette.common.white,
  border: `solid 1px ${varAlpha(theme.vars.palette.common.blackChannel, 0.24)}`,
  background: `linear-gradient(180deg, ${theme.vars.palette.grey[900]}, ${theme.vars.palette.common.black})`,
  [`& .${buttonClasses.startIcon}`]: {
    marginLeft: 0,
  },
}));

// ----------------------------------------------------------------------

export function DownloadApp({ sx, ...other }) {
  const { t } = useLocale();
  const { mode } = useColorScheme();
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  const leftRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState('auto');

  useEffect(() => {
    const updateHeight = () => {
      if (!leftRef.current) return;
      const newHeight = leftRef.current.offsetHeight;
      setLeftHeight((prev) => (prev !== newHeight ? newHeight : prev));
    };

    // Initial height
    updateHeight();

    let frameId = null;

    const handleResize = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <Box
      component="section"
      sx={[
        (theme) => ({
          position: 'relative',
          pt: { xs: 5, md: 10 },
          pb: { xs: 5, md: 10 },
          bgcolor: mode === 'light' ? 'white' : 'background.neutral',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Grid container spacing={{ xs: 5, md: 3 }} sx={{ justifyContent: { md: 'space-between' } }}>
          <Grid size={{ xs: 12, md: 6, lg: 5 }} ref={leftRef}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontSize: '2rem !important' }}>
                {' '}
                {t('Get_Shahen_Now!')}{' '}
              </Typography>
              <Typography sx={{ mt: 3, mb: 5 }}>
                {t('Experience_seamless_logistics_anytime_anywhere')}
              </Typography>
            </Box>

            <Box
              sx={(theme) => ({
                py: 5,
                borderRadius: 2,
                px: { xs: 3, md: 5 },
                border: `solid 1px ${theme.vars.palette.divider}`,
              })}
            >
              <Box
                sx={{
                  gap: 3,
                  display: 'flex',
                  typography: 'h6',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={`${CONFIG.assetsDir}/assets/images/home/qrcode/container-truck-rental-خدمات-الشحن-البري.png`}
                  alt={t('Freight_forwarding')}
                  sx={{ width: 140, height: 140 }}
                />
              </Box>

              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center', // vertical alignment
                  justifyContent: 'center', // horizontal alignment (optional)
                  pt: 4,
                  pb: 2, // padding top & bottom (you can adjust the number)
                  textAlign: 'center', // centers text if multiline
                }}
              >
                {t('ScanQR_code_to_install_on_your_device')}
              </Typography>

              <Divider sx={{ mb: 5, mt: 2, width: 1, borderStyle: 'dashed' }} />

              <Box
                sx={{
                  gap: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <AppStoreButton
                  startIcon={<Iconify width={28} icon="custom:apple" />}
                  caption="Download on the"
                  title="Apple Store"
                />
                <AppStoreButton
                  startIcon={<Iconify width={28} icon="custom:google-play" />}
                  caption="Download from"
                  title="Google Play"
                />
              </Box>
            </Box>
          </Grid>

          <Grid sx={{ textAlign: 'center' }} size={{ xs: 12, md: 6, lg: 6 }}>
            <Box
              component="img"
              alt={t('Competitive_rates_for_corporate_transport_services')}
              src={`${CONFIG.assetsDir}/assets/images/home/qrcode/shahen-app-transportation-logistic.webp`}
              sx={{
                width: '100%',
                height: { md: leftHeight, sx: 300 },
                objectFit: 'cover',
                borderRadius: 2,
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
